import * as _ from 'lodash-es';
import type Quill from 'quill/core';
import type { Op } from 'quill/core';
import { Delta, Parchment } from 'quill/core';
import QuillEditor from '../QuillEditor';
import type { TableDocument } from './types';
import { stringifyCellIdentity } from './utils';
import generateDeltaRandomId from '../utils/generateDeltaRandomId';

let tableCellFormats: Parchment.RegistryDefinition[] = [];
export function registerTableCellFormats(
  formats: Parchment.RegistryDefinition[],
) {
  tableCellFormats = formats;
}

const isEmbedWithScope = (
  insert: Op['insert'],
  scroll: Quill['scroll'],
  scope: typeof Parchment.Scope.BLOCK | typeof Parchment.Scope.INLINE,
): boolean => {
  if (typeof insert === 'object' && insert !== null) {
    const key = Object.keys(insert)[0];
    if (key && scroll.query(key, scope) != null) {
      return true;
    }
  }
  return false;
};

const ensureTrailingNewLine = (
  delta: Delta,
  scroll: Quill['scroll'],
): Delta => {
  const lastOp = _.last(delta.ops);
  const insert = lastOp?.insert;

  if (typeof insert === 'string' && !insert.endsWith('\n')) {
    return delta.insert('\n');
  }
  if (isEmbedWithScope(insert, scroll, Parchment.Scope.INLINE)) {
    return delta.insert('\n');
  }
  return delta;
};

const ensureNewLineBeforeBlockEmbed = (
  delta: Delta,
  scroll: Quill['scroll'],
): Delta => {
  const { ops } = delta;
  const normalizedDelta = new Delta();
  ops.forEach((op, index) => {
    normalizedDelta.push(op);
    if (typeof op.insert === 'string' && !op.insert.endsWith('\n')) {
      const nextInsert = ops[index + 1]?.insert;
      if (isEmbedWithScope(nextInsert, scroll, Parchment.Scope.BLOCK)) {
        normalizedDelta.insert('\n');
      }
    }
  });
  return normalizedDelta;
};

const normalizeDelta = (delta: Delta, scroll: Quill['scroll']): Delta =>
  [ensureNewLineBeforeBlockEmbed, ensureTrailingNewLine].reduce(
    (normalizedDelta, handler) => handler(normalizedDelta, scroll),
    delta,
  );

const getNumericAttribute = (
  node: Element,
  name: string,
  defaultValue: number,
): number => {
  const value = node.getAttribute(name);
  if (!value) return defaultValue;
  const numericValue = parseInt(value, 10);
  return Number.isNaN(numericValue) ? defaultValue : numericValue;
};

const convertFromNode = (node: HTMLTableElement): TableDocument => {
  const quill = new QuillEditor(document.createElement('div'), {
    formats: tableCellFormats,
    content: [{ insert: '\n' }],
    readOnly: true,
    onReadOnlyChange: () => {},
    onActiveBlotChange: () => {},
    onBlotMounted: () => {},
    onBlotUnmounted: () => {},
    onSelectionChange: () => {},
    onTextChange: () => {},
    embedSelectionContainer: null,
  });

  const columnAttributes: { width?: number }[] = [];
  const cells: TableDocument['cells'] = {};
  const trs = [...node.querySelectorAll('tr')];
  const { clipboard } = quill;
  trs.forEach((tr, rowIndex) => {
    const tds = [...tr.querySelectorAll('td, th')];
    let columnIndex = 0;
    tds.forEach((td) => {
      const colspan = getNumericAttribute(td, 'colspan', 1);
      const identity = stringifyCellIdentity(rowIndex, columnIndex);
      const delta = clipboard.convert({ html: td.innerHTML });
      if (delta.ops.length) {
        cells[identity] = {
          content: normalizeDelta(delta, quill.scroll).ops,
        };
      }

      columnIndex += colspan;

      if (rowIndex === 0) {
        const width = getNumericAttribute(td, 'width', 0);
        columnAttributes.push(width ? { width } : {});
        for (let i = 0; i < colspan - 1; i += 1) {
          columnAttributes.push({});
        }
      }
    });
  });

  return {
    cells,
    rows: _.range(trs.length).map(() => ({
      insert: { id: generateDeltaRandomId() },
    })),
    columns: columnAttributes.map((attributes) => ({
      insert: { id: generateDeltaRandomId() },
      ...(_.isEmpty(attributes) ? {} : { attributes }),
    })),
  };
};

export default convertFromNode;
